import { useId, type ReactNode } from 'react';

interface FormFieldProps {
  label: string;
  /** Optional explicit id — overrides the auto-generated one. Useful when
   *  the child control is passed as a plain ReactNode rather than via the
   *  render-prop form. */
  htmlFor?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /** Either a render-prop receiving the generated id, or a plain node when
   *  the caller manages the id/htmlFor wiring themselves. */
  children: ReactNode | ((id: string) => ReactNode);
}

/**
 * Wraps a label + control + (hint | error). Children receive the generated
 * `id` so the label's htmlFor properly associates with the input.
 */
export function FormField({
  label,
  htmlFor,
  hint,
  error,
  required,
  children,
}: FormFieldProps): JSX.Element {
  const generatedId = useId();
  const id = htmlFor ?? generatedId;
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-ink">
        {label}
        {required && <span className="text-danger ml-0.5">*</span>}
      </label>
      {typeof children === 'function' ? (children as (id: string) => ReactNode)(id) : children}
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="text-xs text-ink-muted">{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * Compact summary of validation errors at the top of a form. Useful when a
 * mutation failed for non-field-specific reasons (server-side error,
 * 4xx with a `detail` string).
 */
export function FormBanner({ message }: { message: string }): JSX.Element {
  return (
    <div className="rounded border border-danger bg-surface p-3 text-sm">
      <p className="text-danger">{message}</p>
    </div>
  );
}
